/**
 * The REST client (06 §1.1 `net/api.ts`): same-origin JSON, the session cookie, and one error shape for both our
 * envelope (`{ error: { code, message, fields } }`, 04 §6.1) and Better Auth's (`{ code, message, … }`).
 */

/** A failed call: HTTP status, a machine code, and any extra fields the server sent (e.g. a ban's `reason`). */
export interface ApiFailure {
  ok: false;
  status: number;
  code: string;
  message?: string;
  fields?: Record<string, string>;
  extra: Record<string, unknown>;
}

/** A call's outcome. */
export type ApiResult<T> = { ok: true; status: number; data: T } | ApiFailure;

/** Options of `api`. */
export interface ApiOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

function failure(status: number, body: unknown): ApiFailure {
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const env = b['error'] as { code?: string; message?: string; fields?: Record<string, string> } | undefined;
  if (env && typeof env === 'object' && typeof env.code === 'string') {
    return {
      ok: false,
      status,
      code: env.code,
      ...(env.message ? { message: env.message } : {}),
      ...(env.fields ? { fields: env.fields } : {}),
      extra: {},
    };
  }
  const { code, message, ...extra } = b;
  return {
    ok: false,
    status,
    code: typeof code === 'string' ? code : status === 429 ? 'rate_limited' : `http_${status}`,
    ...(typeof message === 'string' ? { message } : {}),
    extra,
  };
}

/**
 * How long a call may take, body included, before it counts as a network failure: a request that never answers must
 * not leave a form busy (its submit button disabled) for good.
 */
export const API_TIMEOUT_MS = 30_000;

/**
 * Calls `/api…`; never throws (network errors, and calls that take longer than `API_TIMEOUT_MS`, come back as
 * `code: 'network'`).
 */
export async function api<T>(path: string, o: ApiOptions = {}): Promise<ApiResult<T>> {
  const headers: Record<string, string> = { ...o.headers };
  if (o.body !== undefined) {
    headers['content-type'] = 'application/json';
  }
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), API_TIMEOUT_MS);
  let res: Response;
  let text: string;
  try {
    res = await fetch(path, {
      method: o.method ?? (o.body === undefined ? 'GET' : 'POST'),
      headers,
      credentials: 'same-origin',
      signal: abort.signal,
      ...(o.body === undefined ? {} : { body: JSON.stringify(o.body) }),
    });
    text = await res.text();
  } catch {
    return { ok: false, status: 0, code: 'network', extra: {} };
  } finally {
    clearTimeout(timer);
  }
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  return res.ok ? { ok: true, status: res.status, data: body as T } : failure(res.status, body);
}
